import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { KnownDeliveryContainerDtoType, TimelineDto, TimelineEventDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { QueryKeys } from '../../../../core/queries/query-keys.constants';
import { useQueryGetLockerTimeline } from '../../lockers.queries';
import { getDayBounds, groupTimelineEvents, LockerTimelineGroup } from '../logic/locker-timeline.utils';

interface DateWindow {
  from: Date;
  to: Date;
}

function getTodayWindow(): DateWindow {
  return getDayBounds(new Date());
}

function isToday(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
  );
}

function isWindowToday(window: DateWindow): boolean {
  return isToday(window.from);
}

function deduplicateEvents(events: TimelineEventDto[]): TimelineEventDto[] {
  const seen = new Set<string>();
  return events.filter((event) => {
    const key = `${event.timestamp.toISOString()}-${event.type}-${event.title}-${event.delivery?.id ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function useLockerTimeline(deliveryContainerID: string, containerType?: KnownDeliveryContainerDtoType | string) {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDateState] = useState<Date>(() => new Date());
  const [currentWindow, setCurrentWindow] = useState<DateWindow>(getTodayWindow);
  const [olderAccumulated, setOlderAccumulated] = useState<TimelineEventDto[]>([]);
  const [newerAccumulated, setNewerAccumulated] = useState<TimelineEventDto[]>([]);
  const [autoLoadPrevEnabled, setAutoLoadPrevEnabled] = useState(true);
  const [hasOlderEntries, setHasOlderEntries] = useState(true);
  const [noNewerEventsFound, setNoNewerEventsFound] = useState(false);

  const lastDirection = useRef<'older' | 'newer' | null>(null);
  const newerFetchPending = useRef(false);
  const preNewerEventCount = useRef(0);

  const enabled = !!deliveryContainerID;
  const query = useQueryGetLockerTimeline(deliveryContainerID, currentWindow, enabled);

  // Show snackbar when a newer-direction fetch completes with no new events
  useEffect(() => {
    if (newerFetchPending.current && query.isSuccess && query.data) {
      newerFetchPending.current = false;
      const currentEvents = query.data.events ?? [];
      const totalCount = deduplicateEvents([...newerAccumulated, ...currentEvents, ...olderAccumulated]).length;
      if (totalCount <= preNewerEventCount.current && !query.data.nextPageCursor) {
        setNoNewerEventsFound(true);
      }
    }
  }, [query.isSuccess, query.data, newerAccumulated, olderAccumulated]);

  // Auto-load previous page if current window has no events (only for initial/today load)
  useEffect(() => {
    if (autoLoadPrevEnabled && query.data && query.data.events.length === 0 && query.data.prevPageCursor) {
      setAutoLoadPrevEnabled(false);
      lastDirection.current = 'older';
      const { from, to } = getDayBounds(query.data.prevPageCursor);
      setCurrentWindow({ from, to });
    }
  }, [query.data, autoLoadPrevEnabled]);

  // Update hasOlderEntries when an older-direction fetch completes
  useEffect(() => {
    if (query.data && (lastDirection.current === 'older' || lastDirection.current === null)) {
      if (!query.data.prevPageCursor) {
        setHasOlderEntries(false);
      }
    }
  }, [query.data]);

  const setSelectedDate = useCallback((date: Date) => {
    setSelectedDateState(date);
    const { from, to } = getDayBounds(date);
    setCurrentWindow({ from, to });
    setOlderAccumulated([]);
    setNewerAccumulated([]);
    setHasOlderEntries(true);
    setNoNewerEventsFound(false);
    setAutoLoadPrevEnabled(isToday(date));
    lastDirection.current = null;
    newerFetchPending.current = false;
  }, []);

  const loadOlder = useCallback(() => {
    if (query.data?.prevPageCursor) {
      const currentEvents = query.data.events ?? [];
      setOlderAccumulated((prev) => deduplicateEvents([...prev, ...currentEvents]));
      lastDirection.current = 'older';
      const { from, to } = getDayBounds(query.data.prevPageCursor);
      setCurrentWindow({ from, to });
    }
  }, [query.data]);

  const loadNewer = useCallback(async () => {
    setNoNewerEventsFound(false);

    if (query.data?.nextPageCursor) {
      // There is a next page — capture current count, accumulate, and move to that day
      const currentEvents = query.data.events ?? [];
      preNewerEventCount.current = deduplicateEvents([
        ...newerAccumulated,
        ...currentEvents,
        ...olderAccumulated
      ]).length;
      setNewerAccumulated((prev) => deduplicateEvents([...currentEvents, ...prev]));
      lastDirection.current = 'newer';
      newerFetchPending.current = true;
      const { from, to } = getDayBounds(query.data.nextPageCursor);
      setCurrentWindow({ from, to });
    } else if (isWindowToday(currentWindow)) {
      // Already on today with no next cursor — refetch to check for new events
      const previousCount = deduplicateEvents([
        ...newerAccumulated,
        ...(query.data?.events ?? []),
        ...olderAccumulated
      ]).length;

      const queryKey = [
        QueryKeys.DELIVERY_CONTAINERS,
        deliveryContainerID,
        QueryKeys.TIMELINE,
        currentWindow.from.toISOString(),
        currentWindow.to.toISOString()
      ];

      await queryClient.refetchQueries(queryKey);

      const freshData = queryClient.getQueryData<TimelineDto>(queryKey);
      const freshEvents = freshData?.events ?? query.data?.events ?? [];
      const newCount = deduplicateEvents([...newerAccumulated, ...freshEvents, ...olderAccumulated]).length;

      if (newCount <= previousCount) {
        setNoNewerEventsFound(true);
      }
    } else {
      // Past day with no next cursor — jump to today to check for new events
      const currentEvents = query.data?.events ?? [];
      preNewerEventCount.current = deduplicateEvents([
        ...newerAccumulated,
        ...currentEvents,
        ...olderAccumulated
      ]).length;
      setNewerAccumulated((prev) => deduplicateEvents([...currentEvents, ...prev]));
      lastDirection.current = 'newer';
      newerFetchPending.current = true;
      setCurrentWindow(getTodayWindow());
    }
  }, [query.data, newerAccumulated, olderAccumulated, queryClient, deliveryContainerID, currentWindow]);

  const clearNoNewerEvents = useCallback(() => {
    setNoNewerEventsFound(false);
  }, []);

  const groups: LockerTimelineGroup[] = useMemo(() => {
    const currentEvents = query.data?.events ?? [];
    const allEvents = deduplicateEvents([...newerAccumulated, ...currentEvents, ...olderAccumulated]);
    return groupTimelineEvents(allEvents, containerType);
  }, [query.data?.events, olderAccumulated, newerAccumulated, containerType]);

  return {
    groups,
    isLoading: query.isLoading,
    isFetchingOlder: query.isFetching && lastDirection.current === 'older',
    isFetchingNewer: query.isFetching && lastDirection.current === 'newer',
    isError: query.isError,
    selectedDate,
    setSelectedDate,
    loadOlder,
    loadNewer,
    hasOlderEntries,
    noNewerEventsFound,
    clearNoNewerEvents
  };
}
